import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { formatBudgetLabel } from '@modules/build-hub/utils/format-budget';
import {
    formatProjectDateLabel,
    formatScopeLabel,
} from '@modules/build-hub/utils/format-build-hub-attributes';
import type { BuildHubFieldSelection } from '~/modules/enquiry/hooks/use-build-hub-field-selection';

/** A facet of the project the reader can choose to send, with its value. */
export interface ShareChip {
    field: keyof BuildHubFieldSelection;
    label: string;
}

const budgetLabelFor = (buildHub: BuildHubOption): string | null =>
    buildHub.Budget?.Min || buildHub.Budget?.Max
        ? formatBudgetLabel({
              min: buildHub.Budget.Min ?? 0,
              max: buildHub.Budget.Max ?? 0,
          })
        : null;

const placeFor = (buildHub: BuildHubOption): string | null =>
    buildHub.LocationData?.suburb ||
    buildHub.LocationData?.city ||
    buildHub.LocationData?.region ||
    buildHub.Location ||
    null;

/**
 * How a project is described while the reader is choosing between them. Says
 * what the project is, not what would be sent — nothing is being shared yet.
 */
export const projectSubtitle = (buildHub: BuildHubOption): string =>
    [
        buildHub.ProjectScope ? formatScopeLabel(buildHub.ProjectScope) : null,
        buildHub.BuildStage,
        placeFor(buildHub),
    ]
        .filter(Boolean)
        .join(' · ');

/**
 * The same line for a project already attached, showing only what the
 * professional will actually receive. Deselecting every chip has to empty it
 * back to the region — a line still reading "Home renovation · Dreaming"
 * under "0 of 5" tells the reader their choices did nothing.
 *
 * The region stays whatever the chips say: it is locked on by the selection
 * hook, which is what the copy beneath promises.
 */
export const sharedSubtitle = (
    buildHub: BuildHubOption,
    fieldSelection: BuildHubFieldSelection,
): string =>
    [
        fieldSelection.includeScope && buildHub.ProjectScope
            ? formatScopeLabel(buildHub.ProjectScope)
            : null,
        fieldSelection.includeStage ? buildHub.BuildStage : null,
        placeFor(buildHub),
    ]
        .filter(Boolean)
        .join(' · ');

/**
 * The facets on offer, in the order the reader reads them. Only what the
 * project actually holds appears — an empty "Budget" chip on a project with no
 * budget set offers a choice that changes nothing.
 *
 * `includeTitle` and `includeLocation` are locked on by the selection hook and
 * so have no chip; `includeFullAddress` is a precision toggle on a location
 * already being sent, which is why its chip names the exact address rather
 * than the suburb the professional receives either way.
 */
export const shareChipsFor = (buildHub: BuildHubOption): ShareChip[] => {
    const chips: ShareChip[] = [];
    const budget = budgetLabelFor(buildHub);
    const scope = buildHub.ProjectScope
        ? formatScopeLabel(buildHub.ProjectScope)
        : null;

    if (budget) {
        chips.push({ field: 'includeBudget', label: `Budget ${budget}` });
    }
    if (scope) {
        chips.push({ field: 'includeScope', label: scope });
    }
    if (buildHub.BuildStage) {
        chips.push({ field: 'includeStage', label: buildHub.BuildStage });
    }
    if (buildHub.StartDate) {
        chips.push({
            field: 'includeStartDate',
            label: `Starts ${formatProjectDateLabel(buildHub.StartDate, true)}`,
        });
    }
    if (buildHub.EndDate) {
        chips.push({
            field: 'includeEndDate',
            label: `Ends ${formatProjectDateLabel(buildHub.EndDate, true)}`,
        });
    }
    if (buildHub.LocationData?.fullAddress) {
        chips.push({ field: 'includeFullAddress', label: 'Exact address' });
    }
    return chips;
};
